import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../services/api'

interface SyncProgress {
  operation_id: string
  fp_code: string
  franchise_name: string
  system: string
  sync_type: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress_percentage: number
  current_step: string
  records_processed: number
  records_total: number | null
  records_created: number
  records_updated: number
  records_deleted: number
  started_at: string | null
  created_at: string
  completed_at: string | null
  error_message: string | null
  duration_seconds: number | null
  elapsed_seconds: number | null
}

interface UseSyncProgressOptions {
  interval?: number
  onComplete?: (progress: SyncProgress) => void
  onError?: (error: string) => void
}

export const useSyncProgress = (
  operationId: string | null,
  options: UseSyncProgressOptions = {}
) => {
  const { 
    interval = 2000, // Poll every 2 seconds by default
    onComplete,
    onError 
  } = options

  const [progress, setProgress] = useState<SyncProgress | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<NodeJS.Timer | null>(null)

  const fetchProgress = useCallback(async () => {
    if (!operationId) return

    try {
      const response = await api.get(`/sync/progress/${operationId}`)
      const progressData = response.data as SyncProgress

      setProgress(progressData)
      setError(null)

      // Check if sync is complete
      if (['completed', 'failed', 'cancelled'].includes(progressData.status)) {
        setIsPolling(false)
        
        if (progressData.status === 'completed' && onComplete) {
          onComplete(progressData)
        } else if (progressData.status === 'failed' && onError) {
          onError(progressData.error_message || 'Sync failed')
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch progress'
      setError(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    }
  }, [operationId, onComplete, onError])

  // Start polling when operationId is set
  useEffect(() => {
    if (operationId && !isPolling) {
      setIsPolling(true)
      setError(null)
      
      // Fetch immediately
      fetchProgress()

      // Set up polling interval
      intervalRef.current = setInterval(fetchProgress, interval)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [operationId, interval, fetchProgress, isPolling])

  // Stop polling
  const stopPolling = useCallback(() => {
    setIsPolling(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Manually refresh progress
  const refresh = useCallback(() => {
    if (operationId) {
      fetchProgress()
    }
  }, [operationId, fetchProgress])

  return {
    progress,
    isPolling,
    error,
    stopPolling,
    refresh
  }
}

// Hook for batch progress tracking
export const useBatchSyncProgress = (
  operationIds: string[],
  options: UseSyncProgressOptions = {}
) => {
  const { 
    interval = 3000, // Poll every 3 seconds for batch
    onComplete,
    onError 
  } = options

  const [progressMap, setProgressMap] = useState<Record<string, SyncProgress>>({})
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<NodeJS.Timer | null>(null)

  const fetchBatchProgress = useCallback(async () => {
    if (operationIds.length === 0) return

    try {
      const response = await api.post('/sync/progress/batch', {
        operation_ids: operationIds
      })
      
      const newProgressMap: Record<string, SyncProgress> = {}
      response.data.operations.forEach((progress: SyncProgress) => {
        newProgressMap[progress.operation_id] = progress
      })

      setProgressMap(newProgressMap)
      setError(null)

      // Check if all syncs are complete
      const allComplete = operationIds.every(id => {
        const progress = newProgressMap[id]
        return progress && ['completed', 'failed', 'cancelled'].includes(progress.status)
      })

      if (allComplete) {
        setIsPolling(false)
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch batch progress'
      setError(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    }
  }, [operationIds, onError])

  // Start polling when operationIds change
  useEffect(() => {
    if (operationIds.length > 0 && !isPolling) {
      setIsPolling(true)
      setError(null)
      
      // Fetch immediately
      fetchBatchProgress()

      // Set up polling interval
      intervalRef.current = setInterval(fetchBatchProgress, interval)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [operationIds, interval, fetchBatchProgress, isPolling])

  const stopPolling = useCallback(() => {
    setIsPolling(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const refresh = useCallback(() => {
    if (operationIds.length > 0) {
      fetchBatchProgress()
    }
  }, [operationIds, fetchBatchProgress])

  return {
    progressMap,
    isPolling,
    error,
    stopPolling,
    refresh
  }
}